/**
 * Souris et clavier (phase 2, v0.35.0, A78) : deux petits services partagés.
 *
 *  1. **Dernier type d'entrée** (`lastInputKind`) : toucher, stylet, souris ou
 *     clavier, d'après le dernier événement reçu. Sert à ne proposer
 *     « Touchez ou appuyez sur Espace pour lancer » que lorsqu'un clavier est
 *     probable (souris ou clavier), jamais sur un écran tactile.
 *  2. **Pile d'Échap** (`registerEscape`) : chaque fenêtre (lore, Notes de dev,
 *     révélation, confirmation d'abandon) ou panneau repliable (fiche d'un dé)
 *     s'y inscrit à l'ouverture ; `Échap` ferme le plus récent encore présent.
 *
 * Le lancer au clavier (`Espace` / `Entrée`) vit dans la vue de combat, qui a
 * seule le droit de lancer (`src/game3d/battleView3d.ts`). Aucune autre touche.
 */

export type InputKind = 'touch' | 'pen' | 'mouse' | 'keyboard';

let lastKind: InputKind = 'touch';
const kindListeners = new Set<(kind: InputKind) => void>();

/** Un clavier est-il probable ? (souris ou clavier ; jamais après un toucher). */
export function keyboardLikely(kind: InputKind): boolean {
  return kind === 'mouse' || kind === 'keyboard';
}

/** Invite de lancer : « Touchez pour lancer », ou la variante clavier. */
export function throwHintText(kind: InputKind): string {
  return keyboardLikely(kind)
    ? 'Touchez ou appuyez sur Espace pour lancer'
    : 'Touchez pour lancer';
}

/** Type de la dernière entrée (« toucher » tant que rien n'a été reçu). */
export function lastInputKind(): InputKind {
  return lastKind;
}

/** Enregistre une entrée et prévient les abonnés si le type change. */
export function noteInput(kind: InputKind): void {
  if (kind === lastKind) return;
  lastKind = kind;
  for (const fn of [...kindListeners]) fn(kind);
}

/** Abonnement aux changements de type d'entrée. */
export function onInputKindChange(fn: (kind: InputKind) => void): () => void {
  kindListeners.add(fn);
  return () => {
    kindListeners.delete(fn);
  };
}

/** Type d'entrée d'un événement pointeur (`pointerType`). */
export function kindOfPointer(pointerType: string): InputKind {
  if (pointerType === 'mouse') return 'mouse';
  if (pointerType === 'pen') return 'pen';
  return 'touch';
}

// ───────────────────────────────────────────────────────────────────── Échap

interface EscapeEntry {
  /** Élément qui porte la fenêtre : l'entrée tombe d'elle-même s'il quitte le DOM. */
  el: Element | null;
  close: () => void;
}

const escapeStack: EscapeEntry[] = [];

/**
 * Inscrit un élément fermable par `Échap`. `close` ferme la fenêtre (ou replie
 * le panneau). Renvoie le désabonnement, à appeler à la fermeture.
 */
export function registerEscape(el: Element | null, close: () => void): () => void {
  // Les entrées dont l'élément a quitté le DOM (carte reconstruite) sont purgées.
  for (let i = escapeStack.length - 1; i >= 0; i--) {
    const old = escapeStack[i].el;
    if (old && !old.isConnected) escapeStack.splice(i, 1);
  }
  const entry: EscapeEntry = { el, close };
  escapeStack.push(entry);
  return () => {
    const i = escapeStack.indexOf(entry);
    if (i >= 0) escapeStack.splice(i, 1);
  };
}

/**
 * Ferme la fenêtre la plus récente encore présente. Renvoie `true` si quelque
 * chose a été fermé.
 */
export function closeTopmost(): boolean {
  while (escapeStack.length > 0) {
    const entry = escapeStack[escapeStack.length - 1];
    if (entry.el && !entry.el.isConnected) {
      escapeStack.pop();
      continue;
    }
    escapeStack.pop();
    entry.close();
    return true;
  }
  return false;
}

/** Branche les écouteurs globaux (une fois, au démarrage). */
export function installKeyboard(doc: Document = document): () => void {
  const onPointer = (e: PointerEvent): void => noteInput(kindOfPointer(e.pointerType));
  const onKey = (e: KeyboardEvent): void => {
    // Les touches de modification seules (Maj, Ctrl…) ne disent rien du clavier.
    if (e.key === 'Shift' || e.key === 'Control' || e.key === 'Alt' || e.key === 'Meta') return;
    noteInput('keyboard');
    if (e.key === 'Escape' && !e.defaultPrevented && closeTopmost()) e.preventDefault();
  };
  doc.addEventListener('pointerdown', onPointer, true);
  doc.addEventListener('pointermove', onPointer, true);
  doc.addEventListener('keydown', onKey, true);
  return () => {
    doc.removeEventListener('pointerdown', onPointer, true);
    doc.removeEventListener('pointermove', onPointer, true);
    doc.removeEventListener('keydown', onKey, true);
  };
}
